import clsx from 'clsx';

import { uebersetze } from '../../i18n/sprache.js';

import { EmptyState } from './EmptyState.jsx';
import { Skeleton } from './Skeleton.jsx';

/**
 * Thin wrapper around a native HTML table that keeps consistent header styling
 * and handles loading + empty states. Columns is `[{ key, label, align, render }]`.
 *
 * `onRowClick(row)` makes the whole row a click target (detail navigation).
 * Clicks that land on interactive elements inside the row (action buttons,
 * links, form controls) are ignored so row actions keep working.
 *
 * `meta` ist die Hülle der Liste (`{ total, limit, offset }`). Sie ist der
 * Grund, warum es diese Fusszeile gibt: fast jede Liste im Backend hat eine
 * Vorgabegrenze, und wer sie nicht mitgibt, bekommt genau so viele Zeilen -
 * ohne jeden Hinweis, dass es mehr gibt. Auf dem Bildschirm sieht eine
 * abgeschnittene Liste aus wie eine vollständige. Sobald `meta.total` grösser
 * ist als die gezeigten Zeilen, sagt die Tabelle das jetzt hin.
 *
 * `error` trennt zwei Fälle, die bisher gleich aussahen. Scheitert der Abruf,
 * ist `rows` undefined - und die Tabelle behauptete dann "Noch keine
 * Rechnungen erfasst". Das ist keine Ungenauigkeit, sondern eine falsche
 * Auskunft über den Zustand der Firma: leer und nicht erreichbar sind zwei
 * verschiedene Dinge, und nur eines davon darf man glauben.
 */
export function Table({ columns, rows, isLoading, emptyMessage, getRowKey, onRowClick, meta, error }) {
  if (isLoading) {
    return (
      <div className="table-wrap">
        <Skeleton variant="table" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="table-wrap">
        <EmptyState message={uebersetze('tabelle.fehler')}>
          <p className="muted" style={{ maxWidth: '48ch' }}>
            {error.message || uebersetze('fehler.unbekannt', { status: error.status ?? 0 })}
          </p>
        </EmptyState>
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="table-wrap">
        <EmptyState message={emptyMessage} />
      </div>
    );
  }

  const gesamt = Number(meta?.total);
  const gekuerzt = Number.isFinite(gesamt) && gesamt > rows.length;

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={{ textAlign: col.align || 'left' }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={getRowKey ? getRowKey(row) : row.id || index}
              className={clsx(onRowClick && 'row-clickable')}
              onClick={
                onRowClick
                  ? (e) => {
                      // Row actions (buttons, links, inputs) win over navigation.
                      if (e.target.closest('a, button, input, select, textarea, label')) return;
                      onRowClick(row);
                    }
                  : undefined
              }
            >
              {columns.map((col) => (
                <td key={col.key} className={clsx(col.align === 'right' && 'num')}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {gekuerzt ? (
        <p className="table-gekuerzt" role="status">
          {uebersetze('tabelle.gekuerzt', { gezeigt: rows.length, gesamt })}
        </p>
      ) : null}
    </div>
  );
}
